import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';
import EditLocationModal from '../components/EditLocationModal';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n === null ? '—' : Number(n).toLocaleString(); }

function FeaturedBody({ onNav }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [featured,  setFeatured]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(null);
  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState([]);
  const [srchLoad,  setSrchLoad]  = useState(false);
  const [toast,     setToast]     = useState(null);

  function showToast(msg, type) { setToast({ msg, type:type||'ok' }); setTimeout(() => setToast(null), 3000); }

  const load = useCallback(async () => {
    try {
      const { data } = await supabase.rpc('admin_get_featured_locations');
      setFeatured(data || []);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function doSearch() {
    if (!query.trim()) return;
    setSrchLoad(true);
    try {
      const { data } = await supabase.rpc('admin_search_locations', {
        p_query: query.trim(), p_country: null, p_type: null, p_limit: 10, p_offset: 0,
      });
      setResults((data || []).filter(r => !featured.some(f => String(f.id) === String(r.id))));
    } catch (_e) { setResults([]); }
    setSrchLoad(false);
  }

  async function handleFeature(loc, note = '', order = null) {
    try {
      await supabase.rpc('admin_set_featured', {
        p_id: String(loc.id), p_featured: true,
        p_note: note || null,
        p_order: order !== null ? order : featured.length + 1,
      });
      setFeatured(f => [...f, { ...loc, featured_note: note, featured_order: order || f.length + 1 }]);
      setResults(r => r.filter(x => String(x.id) !== String(loc.id)));
      showToast(loc.name + ' added to featured');
    } catch (err) { showToast('Failed: ' + err.message, 'warn'); }
  }

  async function handleUnfeature(id, name) {
    try {
      await supabase.rpc('admin_set_featured', { p_id: String(id), p_featured: false, p_note: null, p_order: null });
      setFeatured(f => f.filter(x => String(x.id) !== String(id)));
      showToast(name + ' removed from featured', 'warn');
    } catch (err) { showToast('Failed: ' + err.message, 'warn'); }
  }

  async function updateOrder(id, newOrder) {
    const parsed = parseInt(newOrder);
    if (isNaN(parsed)) return;
    setFeatured(f => f.map(x => String(x.id) === String(id) ? { ...x, featured_order: parsed } : x));
    try {
      await supabase.rpc('admin_set_featured', {
        p_id: String(id), p_featured: true,
        p_note: featured.find(f => String(f.id)===String(id))?.featured_note || null,
        p_order: parsed,
      });
    } catch (_e) {}
  }

  const sorted = [...featured].sort((a, b) => (a.featured_order||999) - (b.featured_order||999));

  return (
    <div style={{ fontFamily: F }}>
      {toast && <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background:toast.type==='warn'?'#7c3a00':'#1b5e20', color:'#fff', padding:'10px 20px', borderRadius:10, fontSize:'.78rem', fontWeight:500, zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>}

      {editing && <EditLocationModal record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <Insight type="info">
        Featured locations are surfaced first to users in the app. Add them here by searching, set a display order, and optionally add a note. The main app reads the <code style={{ background:'#edf6fb', padding:'1px 5px', borderRadius:4, fontSize:'.85em' }}>featured</code> column from Supabase.
      </Insight>

      {/* Search to add */}
      <Card>
        <CardTitle>Add featured location</CardTitle>
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key==='Enter' && doSearch()}
            placeholder="Search by name or address…"
            style={{ flex:1, fontSize:'.74rem', border:'1px solid #d8eaf4', borderRadius:7, padding:'8px 10px', color:t.tx, outline:'none', fontFamily:F }} />
          <Btn onClick={doSearch} disabled={srchLoad}>{srchLoad ? 'Searching…' : 'Search'}</Btn>
        </div>
        {results.length > 0 && results.map(r => (
          <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'.5px solid rgba(49,79,92,.06)' }}>
            <div>
              <div style={{ fontSize:'.74rem', color:t.tx, fontWeight:500 }}>{r.name}</div>
              <div style={{ fontSize:'.63rem', color:t.mut }}>{r.country}{r.city ? ' · '+r.city : ''}{r.type ? ' · '+r.type : ''}</div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={() => setEditing(r)} style={{ padding:'4px 10px', background:'#edf6fb', color:t.accent, border:'none', borderRadius:6, fontSize:'.65rem', cursor:'pointer', fontFamily:F }}>View</button>
              <button onClick={() => handleFeature(r)} style={{ padding:'4px 10px', background:t.accent, color:'#fff', border:'none', borderRadius:6, fontSize:'.65rem', fontWeight:600, cursor:'pointer', fontFamily:F }}>★ Feature</button>
            </div>
          </div>
        ))}
        {query && results.length === 0 && !srchLoad && <Insight type="info">No results. Try a different search term.</Insight>}
      </Card>

      {/* Current featured list */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <CardTitle>Featured locations ({featured.length})</CardTitle>
          <span style={{ fontSize:'.62rem', color:t.mut }}>Drag order = display order in app</span>
        </div>
        {loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div>
        : sorted.length === 0 ? <Insight type="info">No featured locations yet. Search above to add the first one.</Insight>
        : sorted.map((loc, i) => (
          <div key={loc.id} style={{ display:'flex', gap:10, padding:'10px 0', borderBottom:'.5px solid rgba(49,79,92,.06)', alignItems:'flex-start' }}>
            {/* Order */}
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2, paddingTop:2 }}>
              <input type="number" value={loc.featured_order||i+1}
                onChange={e => updateOrder(loc.id, e.target.value)}
                style={{ width:36, textAlign:'center', fontSize:'.7rem', border:'1px solid #d8eaf4', borderRadius:5, padding:'3px 4px', fontFamily:F, color:t.accent, outline:'none' }} />
              <span style={{ fontSize:'.55rem', color:t.mut }}>order</span>
            </div>
            {/* Gold star */}
            <span style={{ fontSize:'1rem', color:'#9F7E4F', paddingTop:2 }}>★</span>
            {/* Details */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'.76rem', color:t.tx, fontWeight:600 }}>{loc.name}</div>
              <div style={{ fontSize:'.63rem', color:t.mut, marginTop:1 }}>
                {loc.country}{loc.city ? ' · '+loc.city : ''}{loc.type ? ' · '+loc.type : ''}
                {loc.phone && <span> · {loc.phone}</span>}
              </div>
              {loc.featured_note && (
                <div style={{ fontSize:'.65rem', color:t.mut, marginTop:3, background:'#f5fafc', padding:'3px 8px', borderRadius:5, display:'inline-block' }}>
                  {loc.featured_note}
                </div>
              )}
              <div style={{ display:'flex', gap:4, marginTop:5, flexWrap:'wrap' }}>
                {loc.phone       && <span style={{ fontSize:'.58rem', padding:'1px 6px', borderRadius:4, background:'#e8f5e9', color:'#1b5e20' }}>Phone ✓</span>}
                {loc.website     && <span style={{ fontSize:'.58rem', padding:'1px 6px', borderRadius:4, background:'#e8f5e9', color:'#1b5e20' }}>Website ✓</span>}
                {loc.booking_url && <span style={{ fontSize:'.58rem', padding:'1px 6px', borderRadius:4, background:'#e8f5e9', color:'#1b5e20' }}>Booking ✓</span>}
              </div>
            </div>
            {/* Actions */}
            <div style={{ display:'flex', gap:5, flexShrink:0 }}>
              <button onClick={() => setEditing(loc)} style={{ padding:'4px 9px', background:'#edf6fb', color:t.accent, border:'none', borderRadius:6, fontSize:'.65rem', cursor:'pointer', fontFamily:F }}>Edit</button>
              <button onClick={() => handleUnfeature(loc.id, loc.name)} style={{ padding:'4px 9px', background:'#fef0ef', color:'#c0392b', border:'none', borderRadius:6, fontSize:'.65rem', cursor:'pointer', fontFamily:F }}>Remove</button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export default function Featured(props) {
  if (props && props.embed) return <FeaturedBody {...props} />;
  return <ConsoleShell title="App Content"><FeaturedBody {...props} /></ConsoleShell>;
}
